import { ArrowRight } from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { Link, useNavigate } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Button } from '@/common/components/ui/button';
import { useSupplyPoint } from '@/features/supply-points/context/useSupplyPoint';
import { useSidebarManager } from '@/common/components/sidebar-manager/hooks/useSidebarManager';
import { supplyPointQueries } from '@/features/supply-points/queries';
import { productQueries } from '@/features/products/queries';
import { CatalogProductCard } from '@/features/products/components/catalog-product-card/CatalogProductCard';
import { ProductDetailPanel } from '@/features/products/components/product-detail-panel/ProductDetailPanel';
import { isProductTypeLocked } from '@/features/products/utils/isProductTypeLocked';
import { groupVariantsByProductType } from '@/features/products/utils/groupVariantsByProductType';

interface CreateSupplyPointStep4Props {
  supplyPointId: number;
}

export function CreateSupplyPointStep4({ supplyPointId }: CreateSupplyPointStep4Props) {
  const navigate = useNavigate();
  const { setActiveSupplyPointId } = useSupplyPoint();
  const { showSidebar } = useSidebarManager();

  const { data: supplyPoint } = useSuspenseQuery(supplyPointQueries.detail(supplyPointId));
  const { data: allVariants } = useSuspenseQuery(productQueries.variants());
  const { data: products } = useSuspenseQuery(productQueries.products(supplyPointId));

  const filteredVariants = allVariants.filter((v) =>
    v.productType.systemTypes.some((st) => st.id === supplyPoint.systemType)
  );
  const productTypeGroups = groupVariantsByProductType(filteredVariants);
  const canManageProducts =
    supplyPoint.authorizedRole === 'write' || supplyPoint.authorizedRole === 'full';

  const handleFinish = () => {
    setActiveSupplyPointId(supplyPointId);
    void navigate({ to: '/devices' });
  };

  const handleOpenPanel = (variantId: number) => {
    showSidebar(
      <ProductDetailPanel
        variantId={variantId}
        supplyPointId={supplyPointId}
        canManageProducts={canManageProducts}
      />
    );
  };

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      <div className="px-6 py-5 border-b border-border">
        <h2 className="text-base font-semibold">
          <Trans>Recommended products</Trans>
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          <Trans>
            Your supply point is ready. See which products can bring you the most value.
          </Trans>
        </p>
      </div>

      <div className="px-6 py-6 space-y-5">
        <p className="text-sm text-muted-foreground">
          <Trans>
            Based on your configuration, we selected products that will help you get the most out of
            your supply point. You can manage products at any time in the{' '}
          </Trans>
          <Link
            to="/products"
            className="text-primary hover:underline font-medium"
            onClick={() => setActiveSupplyPointId(supplyPointId)}
          >
            <Trans>Products</Trans>
          </Link>
          <Trans> section.</Trans>
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[...productTypeGroups.entries()].map(([, typeVariants]) => {
            const representative = typeVariants[0];
            const activeProduct =
              products.find((p) => typeVariants.some((v) => v.id === p.variant)) ?? null;
            const isLocked =
              !activeProduct && isProductTypeLocked(typeVariants, filteredVariants, products);
            return (
              <CatalogProductCard
                key={representative.productType.name}
                variant={representative}
                activeProduct={activeProduct}
                isLocked={isLocked}
                onClick={() => handleOpenPanel(representative.id)}
              />
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-muted/30">
        <span className="text-xs text-muted-foreground">
          <Trans>Step 4 of 4 · Setup complete</Trans>
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" asChild onClick={() => setActiveSupplyPointId(supplyPointId)}>
            <Link to="/products" className="gap-1.5">
              <Trans>View all products</Trans>
              <ArrowRight size={14} />
            </Link>
          </Button>
          <Button onClick={handleFinish}>
            <Trans>Finish setup</Trans>
          </Button>
        </div>
      </div>
    </div>
  );
}
